import { TrackedChange } from 'models/changes'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React, { useRef } from 'react'
import { CellMeasurer, List } from 'react-virtualized'
import { ChangesCard } from '../../ChangesCard'
import useBetaWorkspaceToggle from '../../hooks/useBetaWorkspaceToggle'
import { useListContainerHeight } from '../../hooks/useListContainerHeight'
import { useTrackChangesCache } from '../../hooks/useTrackChangesCache'
import { useStyles } from '../../TrackChangesPane'

interface TrackChangesListProps {
    changes: TrackedChange[] | null | undefined
    selectedTopic?: Partial<ProductFamilyStudioManagement>
}

export const TrackChangesList = React.memo(
    ({ changes, selectedTopic }: TrackChangesListProps) => {
        const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
        const classes = useStyles({
            isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
        })
        const [selectedTracktip, setSelectedTracktip] = React.useState<
            string | undefined
        >(undefined)
        const containerRef = useRef<HTMLDivElement>(null)
        const { height: listHeight } = useListContainerHeight(
            containerRef,
            changes?.length ?? 0
        )

        const handleTrackTipSelect = React.useCallback(
            (changeId: string | undefined) => {
                setSelectedTracktip(changeId)
            },
            []
        )

        const { cache, setCacheHeight, clearCacheRow } = useTrackChangesCache(
            changes ?? []
        )

        // Clear all cache when changes data changes to prevent overlapping
        React.useEffect(() => {
            cache.clearAll()
        }, [changes, cache])

        return (
            <div ref={containerRef} className={classes.trackChangesWrapper}>
                <List
                    height={listHeight}
                    width={268}
                    items={changes}
                    rowHeight={cache.rowHeight}
                    rowCount={changes?.length ?? 0}
                    data-testid="changesList"
                    id="trackChangeListWrapper"
                    rowRenderer={({ index, style, key, parent }) => {
                        if (!changes) return null
                        return (
                            <CellMeasurer
                                cache={cache}
                                columnIndex={0}
                                key={key}
                                parent={parent}
                                rowIndex={index}
                            >
                                {({ measure, registerChild }) => (
                                    <div
                                        ref={registerChild}
                                        style={style}
                                        className={classes.cardList}
                                    >
                                        <ChangesCard
                                            change={changes?.[index]!}
                                            selectedTopic={selectedTopic}
                                            selectTracktip={
                                                handleTrackTipSelect
                                            }
                                            isSelected={
                                                changes?.[index]?.id ===
                                                selectedTracktip
                                            }
                                            setCacheHeight={setCacheHeight}
                                            clearCacheRow={clearCacheRow}
                                            rowIndex={index}
                                            measure={measure}
                                            totalChanges={changes?.length}
                                        />
                                    </div>
                                )}
                            </CellMeasurer>
                        )
                    }}
                />
            </div>
        )
    }
)
